import {
  ColDef,
  GetContextMenuItems,
  SideBarDef,
  themeQuartz,
} from "ag-grid-community";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  ImportRelocationType,
  RelocationItemInput,
  RelocationStatus,
} from "@/gql/graphql";
import { UniqueValueCell } from "@/app/Import/Components/UniqueValueCell";
import {
  ArrowPathIcon,
  SquaresPlusIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import { Button } from "@/components/catalyst/button";
import { useUpdateImportSettings } from "@/app/Import/Utils/useUpdateImportSettings";
import { VehicleRenderCell } from "@/app/Import/Components/VehicleRenderCell";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { supplierOfficeListQuery } from "@/app/Offices/GraphQL/supplierOfficeListQuery";
import { vehicleListQuery } from "@/app/Vehicles/GraphQL/vehicleListQuery";
import { OfficeRenderCell } from "@/app/Import/Components/OfficeRenderCell";
import { supplierRecordQuery } from "@/app/Suppliers/GraphQL/supplierRecordQuery";
import { FutureDateCell } from "@/app/Import/Components/FutureDateCell";
import { CurrencyRenderCell } from "@/app/Import/Components/CurrencyRenderCell";
import { importSettingRecord } from "@/app/Import/GraphQL/updateImportSettingsMutation";
import { useImportState } from "@/app/Import/Stores/useImportState";
import { importRelocationsMutation } from "@/app/Import/GraphQL/importMutations";
import { useMatch, useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { MissingCodeWarningBanner } from "@/app/Import/Components/MissingCodeWarningBanner";
import { getAndValidateRows } from "@/app/Import/Utils/getAndValidateRows";
import { useTranslations } from "use-intl";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { SaveIcon } from "lucide-react";
import { toast } from "sonner";
import { useAgGridLocalization } from "@/lib/Hooks/useAgGridLocalization";
import {
  defaultColOrder,
  defaultHiddenCols,
} from "@/app/Import/Utils/defaultColumnOrder";
import {
  getImportOfficeQueryVariables,
  getImportVehicleQueryVariables,
} from "@/app/Import/Utils/getImportTableQueryVariables";

type ImportTableProps = {
  supplierId: string;
};

export function ImportTable({ supplierId }: ImportTableProps) {
  const gridRef = useRef<AgGridReact<RelocationItemInput>>(null);
  const t = useTranslations("import");
  const agGridLocale = useAgGridLocalization();

  const { data: supplierRes } = useSuspenseGqlQuery(supplierRecordQuery, {
    id: supplierId,
  });
  const { supplier } = supplierRes;

  const { data: importSettingRes } = useSuspenseGqlQuery(importSettingRecord, {
    supplierId,
  });

  const { isPending, hasChanges, handleSave, handleReset, onGridUpdate } =
    useUpdateImportSettings(
      supplierId,
      gridRef,
      importSettingRes.importSetting?.grid_state,
    );

  const { data: vehicleRes } = useSuspenseGqlQuery(
    vehicleListQuery,
    getImportVehicleQueryVariables(supplierId),
  );

  const vehicles = vehicleRes.vehicles.data;

  const { data: officesRes } = useSuspenseGqlQuery(
    supplierOfficeListQuery,
    getImportOfficeQueryVariables(supplierId),
  );

  const offices = officesRes.supplierOffices.data;

  const vehicleCodes = vehicles.map((vehicle) => vehicle.code);
  const officeCodes = offices.map((office) => office.code);

  // Each Column Definition results in one Column.
  const columnDefs = useMemo<ColDef<RelocationItemInput>[]>(
    () => [
      {
        field: "quantity",
        cellDataType: "number",
        headerName: t("columns.quantity"),
        hide: true,
      },
      {
        field: "line",
        headerName: t("columns.line"),
        cellRenderer: UniqueValueCell,
      },
      {
        field: "vehicle_code",
        cellEditor: "agRichSelectCellEditor",
        cellRenderer: VehicleRenderCell,
        headerName: t("columns.vehicle_code"),
        cellRendererParams: {
          supplier,
        },
        cellEditorParams: {
          values: vehicleCodes,
          cellHeight: 20,
        },
      },
      {
        field: "departure_office_code",
        headerName: t("columns.departure_office_code"),
        cellEditor: "agRichSelectCellEditor",
        cellEditorParams: {
          values: officeCodes,
          cellHeight: 20,
        },
        cellRenderer: OfficeRenderCell,
        cellRendererParams: {
          supplier,
          officeType: "departure",
        },
      },
      {
        field: "delivery_office_code",
        headerName: t("columns.delivery_office_code"),
        cellEditor: "agRichSelectCellEditor",
        cellEditorParams: {
          values: officeCodes,
          cellHeight: 20,
        },
        cellRenderer: OfficeRenderCell,
        cellRendererParams: {
          supplier: supplier,
          officeType: "delivery",
        },
      },
      {
        field: "available_from",
        cellDataType: "dateString",
        cellRenderer: FutureDateCell,
        headerName: t("columns.available_from"),
        cellEditor: "agDateStringCellEditor",
        cellEditorParams: {
          min: dayjs().format("YYYY-MM-DD"),
        },
      },
      {
        field: "available_to",
        cellDataType: "dateString",
        headerName: t("columns.available_to"),
        cellRenderer: FutureDateCell,
        cellEditor: "agDateStringCellEditor",
        cellEditorParams: (params: any) => {
          const availableFrom = params.data?.available_from;
          return {
            min: availableFrom
              ? dayjs(availableFrom).format("YYYY-MM-DD")
              : dayjs().format("YYYY-MM-DD"),
          };
        },
      },
      {
        field: "hire_units_allowed",
        cellDataType: "number",
        headerName: t(`relocation_days_label.${supplier.hire_unit_type}`),
      },
      {
        field: "minimum_hire_units",
        cellDataType: "number",
        headerName: t(`minimum_hire_label.${supplier.hire_unit_type}`),
        hide: true,
      },
      {
        field: "extra_hire_units_allowed",
        cellDataType: "number",
        headerName: t(`extra_days_label.${supplier.hire_unit_type}`),
      },
      {
        field: "distance_allowed",
        headerName: t(`mileage_label.${supplier.measurement}`),
        hide: true,
      },
      {
        field: "fuel_amount",
        cellDataType: "number",
        headerName: t("columns.fuel_amount"),
        cellRenderer: CurrencyRenderCell,
        cellRendererParams: {
          currency: supplier.currency,
        },
      },
      {
        field: "fuel_note",
        headerName: t("columns.fuel_note"),
        hide: true,
      },
      {
        field: "ferry_note",
        headerName: t("columns.ferry_note"),
        hide: true,
      },
      {
        field: "inclusion_note",
        headerName: t("columns.inclusion_note"),
        hide: true,
      },
      {
        field: "hire_unit_rate",
        headerName: t("columns.hire_unit_rate"),
        hide: true,
      },
      {
        field: "extra_hire_unit_supplier_net_rate",
        headerName: t(
          `extra_hire_unit_net_rate_label.${supplier.hire_unit_type}` as any,
        ),
        hide: true,
      },
      {
        field: "preparation_charge_amount",
        headerName: t("columns.preparation_charge_amount"),
        hide: true,
      },
      {
        field: "preparation_charge_note",
        headerName: "Preparation charge note",
        hide: true,
      },
    ],
    [],
  );

  if (!import.meta.env.PROD) {
    columnDefs.forEach((columnDef, index) => {
      if (columnDef.field !== defaultColOrder[index]) {
        console.error(
          `ORDER IS INCORRECT - expected ${defaultColOrder[index]} but got ${columnDef.field}`,
        );
      }

      if (
        columnDef.hide &&
        columnDef.field &&
        !defaultHiddenCols.includes(columnDef.field)
      ) {
        console.error(`HIDDEN COLUMN IS NOT MAPPED - ${columnDef.field}`);
      }
    });
  }

  const { relocations, reset } = useImportState();
  const rowData = useMemo<RelocationItemInput[]>(() => {
    return relocations;
  }, []);

  //Reset the imported spreadsheet when the component unmounts
  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  const defaultColDef: ColDef<any> = useMemo(
    () => ({
      editable: true,
      sortable: false,
      resizable: true,
      enableCellChangeFlash: true,
      enablePivot: false,
      rowGroup: false,
    }),
    [],
  );

  const sideBar = useMemo<SideBarDef>(() => {
    return {
      hiddenByDefault: false,
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivotMode: true,
          },
        },
      ],
    } satisfies SideBarDef;
  }, []);

  const getSelectedRows = useCallback(() => {
    const range = gridRef.current?.api.getCellRanges();
    const rowStart = range?.at(0)?.startRow?.rowIndex;
    const rowEnd = range?.at(0)?.endRow?.rowIndex;

    if (rowStart === undefined || rowStart === null) return;
    if (rowEnd === undefined || rowEnd === null) return;

    const start = rowStart < rowEnd ? rowStart : rowEnd;
    const end = rowStart < rowEnd ? rowEnd : rowStart;

    return Array.from({ length: end - start + 1 }, (_, i) => i + start!)
      .map((rowIndex) => {
        return gridRef.current?.api.getDisplayedRowAtIndex(rowIndex)?.data;
      })
      .filter(Boolean);
  }, []);

  const deleteSelected = useCallback(() => {
    const rows = getSelectedRows();
    gridRef.current?.api.applyTransaction({
      remove: rows,
    });
  }, [gridRef]);

  const getContextMenuItems: GetContextMenuItems<RelocationItemInput, any> =
    useCallback(() => {
      return [
        "copy",
        "paste",
        {
          name: "Add row",
          icon: '<span class="ag-icon ag-icon-plus" unselectable="on" role="presentation"></span>',
          action: () => {
            gridRef.current?.api.applyTransaction({
              add: [{} as any],
            });
          },
        },
        {
          name: "Delete row(s)",
          icon: '<span class="ag-icon ag-icon-not-allowed" unselectable="on" role="presentation"></span>',
          action: deleteSelected,
        },
      ];
    }, []);

  const onUpdate = useCallback(() => {
    onGridUpdate();
  }, [onGridUpdate]);

  return (
    <div className="ag-theme-quartz flex h-full w-full flex-col">
      <MissingCodeWarningBanner offices={offices} vehicles={vehicles} />

      <AgGridReact<RelocationItemInput>
        theme={themeQuartz}
        /**
         *  AG-Grid does not update when the reference  processCellFromClipboard reference is changed (it is internally memoized)
         * To force the grid to update, we need to change the key of the grid component
         **/
        key={importSettingRes.importSetting?.date_format}
        className="grow"
        ref={gridRef as any}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        gridOptions={{
          cellSelection: true,
          initialState: importSettingRes.importSetting?.grid_state,
          localeText: agGridLocale,
          processCellFromClipboard: (params) => {
            if (
              params.column.getColId() === "available_from" ||
              params.column.getColId() === "available_to"
            ) {
              try {
                const formats = [
                  "YYYY-MM-DD",
                  importSettingRes.importSetting?.date_format,
                ].filter((f): f is string => Boolean(f));
                const parsed = dayjs(params.value, formats);
                if (!parsed.isValid()) {
                  console.warn(
                    `Failed to parse date value "${params.value}" in column ${params.column.getColId()}`,
                  );
                  return params.value;
                }
                return parsed.format("YYYY-MM-DD");
              } catch (error) {
                console.error(
                  `Error parsing date in column ${params.column.getColId()}:`,
                  error,
                );
                return params.value;
              }
            }

            if (
              params.column.getColId() === "fuel_amount" ||
              params.column.getColId() === "charge_amount" ||
              params.column.getColId() === "hire_units_allowed" ||
              params.column.getColId() === "minimum_hire_units" ||
              params.column.getColId() === "extra_hire_units_allowed"
            ) {
              if (params.value === "") {
                return null;
              }

              try {
                const stringValue = String(params.value);
                const isNegative = stringValue.includes("-");
                const digitsAndDecimal = stringValue.replace(/[^\d.]/g, "");
                const parts = digitsAndDecimal.split(".");
                const cleaned =
                  parts.length > 1
                    ? `${parts[0]}.${parts.slice(1).join("")}`
                    : digitsAndDecimal;
                const parsed = parseFloat(
                  isNegative && !cleaned.startsWith("-")
                    ? `-${cleaned}`
                    : cleaned,
                );

                if (isNaN(parsed)) {
                  console.warn(
                    `Failed to parse numeric value "${params.value}" in column ${params.column.getColId()}`,
                  );
                  return null;
                }

                return parsed;
              } catch (error) {
                console.error(
                  `Error parsing numeric value in column ${params.column.getColId()}:`,
                  error,
                );
                return null;
              }
            }

            return params.value;
          },
        }}
        animateRows={true}
        getContextMenuItems={getContextMenuItems}
        onColumnMoved={onUpdate}
        onColumnResized={onUpdate}
        onColumnValueChanged={onUpdate}
        onColumnVisible={onUpdate}
        onColumnPinned={onUpdate}
        onColumnRowGroupChanged={onUpdate}
        onColumnPivotChanged={onUpdate}
        sideBar={sideBar}
      />

      <div className="relative z-10 border-t border-gray-200 bg-white px-6 py-4">
        <div className="flex justify-between gap-3">
          <div className="flex gap-3">
            <Button
              plain
              onClick={handleReset}
              disabled={!hasChanges}
              LeadingIcon={XMarkIcon}
            >
              {t("footer.reset")}
            </Button>
            <Button
              outline
              onClick={handleSave}
              LeadingIcon={SaveIcon}
              isLoading={isPending}
              disabled={!hasChanges}
            >
              {t("footer.save_template")}
            </Button>
          </div>
          <div className="flex gap-3">
            <UploadButtons grid={gridRef.current!} />
          </div>
        </div>
      </div>
    </div>
  );
}

function UploadButtons({ grid }: { grid: AgGridReact<RelocationItemInput> }) {
  const navigate = useNavigate();
  const t = useTranslations("import");

  const { mutateAsync: importRelocations } = useGqlMutation(
    importRelocationsMutation,
  );
  const { params } = useMatch({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/import/$importSupplierId",
  });

  const { data: importSettingRes } = useSuspenseGqlQuery(importSettingRecord, {
    supplierId: params.importSupplierId,
  });

  const [loadingAppend, setLoadingAppend] = useState(false);
  const [loadingReplace, setLoadingReplace] = useState(false);

  const handleAppend = async () => {
    setLoadingAppend(true);
    try {
      const rows = getAndValidateRows(
        grid,
        !!importSettingRes.importSetting?.merge_duplicates,
      );

      if (rows === false) {
        return;
      }

      await importRelocations({
        input: {
          type: ImportRelocationType.Append,
          supplierId: params.importSupplierId,
          relocations: rows,
        },
      });

      await navigate({
        from: "/$tenant/$orgId/supplier/{-$supplierId}",
        to: "/$tenant/$orgId/supplier/{-$supplierId}/relocations",
        params: (prev) => ({
          ...prev,
          supplierId: params.importSupplierId,
        }),
        search: {
          relocationStatus: [RelocationStatus.Ready, RelocationStatus.Paused],
        },
      });
    } catch (error) {
      console.error("Error appending relocations:", error);
      toast.error("Failed to append relocations", {
        description:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
    } finally {
      setLoadingAppend(false);
    }
  };

  const handleReplace = async () => {
    setLoadingReplace(true);
    try {
      const rows = getAndValidateRows(
        grid,
        !!importSettingRes.importSetting?.merge_duplicates,
      );

      if (rows === false) {
        return;
      }

      await importRelocations({
        input: {
          type: ImportRelocationType.New,
          supplierId: params.importSupplierId,
          relocations: rows,
        },
      });

      await navigate({
        from: "/$tenant/$orgId/supplier/{-$supplierId}",
        to: "/$tenant/$orgId/supplier/{-$supplierId}/relocations",
        params: (prev) => ({
          ...prev,
          supplierId: params.importSupplierId,
        }),
        search: {
          relocationStatus: [RelocationStatus.Ready],
        },
      });
    } catch (error) {
      console.error("Error replacing relocations:", error);
      toast.error("Failed to replace relocations", {
        description:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
    } finally {
      setLoadingReplace(false);
    }
  };

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            outline
            onClick={handleAppend}
            disabled={loadingAppend || loadingReplace}
          >
            {loadingAppend ? (
              <Spinner className="mr-2 inline h-5 w-5" />
            ) : (
              <SquaresPlusIcon className="mr-2 inline h-5 w-5" />
            )}
            {t("footer.append")}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t("footer.append_tooltip")}</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            onClick={handleReplace}
            disabled={loadingAppend || loadingReplace}
          >
            {loadingReplace ? (
              <Spinner className="mr-2 inline h-5 w-5" />
            ) : (
              <ArrowPathIcon className="mr-2 inline h-5 w-5" />
            )}
            {t("footer.replace")}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t("footer.replace_tooltip")}</TooltipContent>
      </Tooltip>
    </>
  );
}
